/**
 * Asset resolution.
 *
 * Assets arrive from ccassets via the importer; this module resolves the code alias
 * to a path and reports honestly when one is absent. Nothing here invents artwork:
 * a missing asset degrades the stage that needs it, it never substitutes a placeholder
 * (ENG-069).
 */
export type AssetAlias =
  | 'startup_transition_1080p'
  | 'startup_transition_720p'
  | 'startup_poster'
  | 'startup_endframe'
  | 'level2_end_transition_1080p'
  | 'level2_end_poster'
  | 'level2_end_endframe'
  | 'level3_end_transition_720p'
  | 'level3_end_poster'
  | 'level3_end_endframe'
  | 'crt_power_bloom'
  | 'crt_signal_lock'
  | 'castle_codi_model'
  | 'castle_spark_model'
  | 'castle_spark_crowd_model'
  | 'castle_spark_far_model'
  | 'castle_princess_model'
  | 'castle_sample_model'
  | 'castle_drive_model'
  | 'castle_track'
  | 'castle_rave_track'
  | 'castle_cover'
  | 'castle_intro'
  | 'codi_life';

const BASE = '/assets';

const PATHS: Record<AssetAlias, string> = {
  startup_transition_1080p: `${BASE}/TRANSITIONS/CC_TRANSITION_001_ENTER_THE_BROADCAST_v001_1080p.mp4`,
  startup_transition_720p: `${BASE}/TRANSITIONS/CC_TRANSITION_001_ENTER_THE_BROADCAST_v001_720p.mp4`,
  startup_poster: `${BASE}/TRANSITIONS/CC_TRANSITION_001_ENTER_THE_BROADCAST_v001_poster.jpg`,
  startup_endframe: `${BASE}/TRANSITIONS/CC_TRANSITION_001_ENTER_THE_BROADCAST_v001_endframe.webp`,
  level2_end_transition_1080p: `${BASE}/TRANSITIONS/CC_TRANSITION_002_CRASH_THE_RAVE_v001_1080p.mp4`,
  level2_end_poster: `${BASE}/TRANSITIONS/CC_TRANSITION_002_CRASH_THE_RAVE_v001_poster.jpg`,
  level2_end_endframe: `${BASE}/TRANSITIONS/CC_TRANSITION_002_CRASH_THE_RAVE_v001_endframe.webp`,
  // 720p, not 1080p: the master is 1280x720 and upscaling adds bytes, not detail.
  // Still more real picture than 002, which is 1080p but 58% black pillarbox.
  level3_end_transition_720p: `${BASE}/TRANSITIONS/CC_TRANSITION_003_ENTER_THE_STUDIO_v001_720p.mp4`,
  level3_end_poster: `${BASE}/TRANSITIONS/CC_TRANSITION_003_ENTER_THE_STUDIO_v001_poster.jpg`,
  level3_end_endframe: `${BASE}/TRANSITIONS/CC_TRANSITION_003_ENTER_THE_STUDIO_v001_endframe.webp`,
  crt_power_bloom: `${BASE}/CRT/CC_CRT_001_POWER_BLOOM_v001_1080p.mp4`,
  crt_signal_lock: `${BASE}/CRT/CC_CRT_002_SIGNAL_LOCK_v001_1080p.mp4`,
  castle_codi_model: `${BASE}/CODEINE_CASTLE/CODI_HQ_WEB.glb`,
  castle_spark_model: `${BASE}/CODEINE_CASTLE/SPARK2_HQ.glb`,
  castle_spark_crowd_model: `${BASE}/CODEINE_CASTLE/SPARK2_CROWD.glb`,
  castle_spark_far_model: `${BASE}/CODEINE_CASTLE/SPARK2_CROWD_FAR.glb`,
  castle_princess_model: `${BASE}/CODEINE_CASTLE/DJ_PRINCESS.glb`,
  castle_sample_model: `${BASE}/CODEINE_CASTLE/CASTLE_SAMPLE.glb`,
  castle_drive_model: `${BASE}/CODEINE_CASTLE/GOLDEN_USB_DRIVE.glb`,
  castle_track: `${BASE}/CODEINE_CASTLE/CASTLE_TRACK.mp3`,
  castle_rave_track: `${BASE}/CODEINE_CASTLE/RAVE_TRACK.mp3`,
  castle_cover: `${BASE}/CODEINE_CASTLE/CASTLE_COVER.jpg`,
  castle_intro: `${BASE}/CODEINE_CASTLE/CASTLE_INTRO.mp4`,
  codi_life: `${BASE}/CODEINE_CASTLE/CODI_LIFE.png`,
};

export const assetUrl = (a: AssetAlias): string => PATHS[a];

/** Media kind a genuine response for this extension must carry (video/*, image/*). */
const EXPECTED_KIND: Record<string, 'video' | 'image'> = {
  '.mp4': 'video',
  '.webm': 'video',
  '.webp': 'image',
  '.jpg': 'image',
  '.jpeg': 'image',
  '.png': 'image',
};

/**
 * HEAD probe. Used to degrade a stage rather than fail the journey.
 *
 * `response.ok` alone is not enough: a dev server's SPA fallback (and some static
 * hosts) answer a missing path with 200 + index.html rather than a 404. The
 * Content-Type must actually match the asset's own media kind — a text/html body
 * never does — or a missing asset is silently treated as present.
 *
 * `onMiss` REPORTS WHY, AND IS OPTIONAL BECAUSE A MISS IS USUALLY EXPECTED.
 *
 * This returns false for three unrelated reasons — the host said no, the host
 * said yes and sent the wrong kind of thing, or the request never completed —
 * and it used to collapse all three into one bare `false`. That is fine for the
 * two callers that ask "is this optional decoration here?" (`stages/driver.ts`
 * picks among alternatives, `stages/scene.ts` falls back to a void backdrop):
 * for them a miss is a normal answer, and reporting it would be crying wolf.
 *
 * It is NOT fine for a caller whose whole beat depends on the answer. The owner
 * lost Level 02's cinematic on a phone and the build could not say which of the
 * three had happened, because nothing recorded it. Callers that care pass a
 * reporter; callers that do not are unchanged, and stay silent.
 */
export async function assetExists(
  a: AssetAlias,
  onMiss?: (reason: string) => void,
): Promise<boolean> {
  const url = PATHS[a];
  try {
    const r = await fetch(url, { method: 'HEAD' });
    if (!r.ok) { onMiss?.(`HTTP ${r.status} for ${url}`); return false; }
    const ext = url.slice(url.lastIndexOf('.')).toLowerCase();
    const kind = EXPECTED_KIND[ext];
    if (!kind) return true;
    const ct = r.headers.get('content-type') ?? '';
    if (ct.startsWith(`${kind}/`)) return true;
    onMiss?.(`content-type "${ct}" is not ${kind} for ${url}`);
    return false;
  } catch (e) {
    /* The interesting one, and the reason this parameter exists: a probe that
       never completed looks identical to a missing file from the outside, but
       means the asset is present and the network dropped. */
    onMiss?.(`probe failed: ${e instanceof Error ? e.message : String(e)}`);
    return false;
  }
}
